// 가맹점주 화면 API 클라이언트. 계약: backend docs/frontend-admin-v6-api-handoff.md §14(2026-10-12)
// 신원은 가맹점 토큰(Authorization: Bearer) — 탭을 닫으면 로그아웃되도록 sessionStorage 에 둔다.
import axios from "axios";

export const TOKEN_KEY = "customer_token";
export const ME_KEY = "customer_me";
export const AUTH_EVENT = "customer-auth";
const TRIES = 5;

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export function getMe() {
    try { return JSON.parse(sessionStorage.getItem(ME_KEY)) || null; } catch { return null; }
}
export function saveLogin(out) {
    sessionStorage.setItem(TOKEN_KEY, out.access_token);
    sessionStorage.setItem(ME_KEY, JSON.stringify(out.customer || null));
}
export function clearAuth() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ME_KEY);
}
export const authHeaders = () => { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; };
const emit = (reason) => window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { reason } }));

// 토큰이 없거나 틀리거나 만료 — 다시 로그인. 비밀번호 변경의 401 invalid_credentials 는 로그아웃 신호가 아니다(§14-0)
export const authLost = (status, code) => status === 401 && (code === "token_required" || code === "invalid_token");

// 늦게 온 응답이 그 사이 새로 받은 토큰을 지우지 않게: 보낸 토큰이 지금 토큰일 때만 끊는다
function dropIfCurrent(sentAuth) {
    const current = getToken();
    if (!sentAuth || !current || sentAuth === `Bearer ${current}`) { clearAuth(); emit("expired"); }
}

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });
api.interceptors.request.use((config) => {
    config.headers = { ...(config.headers || {}), ...authHeaders() };
    return config;
});
export function handleAuthError(e) {
    const r = e?.response;
    if (authLost(r?.status, r?.data?.detail?.code)) dropIfCurrent(e?.config?.headers?.Authorization);
    return Promise.reject(e);
}
api.interceptors.response.use((r) => r, handleAuthError);
export const http = api;

// fetch 를 쓰는 곳(질의응답·음성·접수) — 같은 머리말과 같은 끊김 처리
export async function authFetch(path, opts = {}) {
    const sent = authHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, { ...opts, headers: { ...(opts.headers || {}), ...sent } });
    if (res.status === 401) {
        let code = null;
        try { code = (await res.clone().json())?.detail?.code; } catch { /* 본문 없음 */ }
        if (authLost(401, code)) dropIfCurrent(sent.Authorization);
    }
    return res;
}

export async function login(businessNumber, password) {
    const { data } = await api.post("/auth/login", { business_number: String(businessNumber).replace(/\D/g, ""), password });
    saveLogin(data);
    return data;
}
export const changePassword = (current, next) =>
    api.patch("/auth/password", { current_password: current, new_password: next }).then((r) => r.data);

// 로그인 실패 문구: 남은 횟수(n/5) · 잠김(N분 뒤) · 스토라넷 장애·네트워크(잠시 뒤)
export function loginErrorText(e, t) {
    const st = e?.response?.status, d = e?.response?.data?.detail;
    if (st === 423) return t("login.locked").replace("{min}", Math.max(1, Math.ceil((d?.retry_after || 0) / 60)));
    if (st === 401) return typeof d?.remaining_attempts === "number" ? `${t("login.fail")} (${TRIES - d.remaining_attempts}/${TRIES})` : t("login.fail");
    if (st && st < 500) return t("login.fail");
    return t("login.unavailable");
}

// 대화(§14-3): 웹 챗봇은 channel "web"
export const startSession = () =>
    api.post("/chat/sessions", { title: "웹 상담", channel: "web" }).then((r) => r.data?.id ?? null);

// 질의응답. 실패는 {status, code, message, newSession} 오류 — 최대 메시지(409)·없는/지난 대화(404)면 새 대화를 열어야 한다
export async function ask(sessionId, question, lang) {
    const res = await authFetch(`/llm/chat/sessions/${sessionId}/qa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, lang, channel: "web" }),
    });
    let body = null;
    try { body = await res.json(); } catch { /* 본문 없음 */ }
    if (res.ok) return body;
    const d = body?.detail;
    const code = d && typeof d === "object" ? d.code : null;
    throw Object.assign(new Error(`qa ${res.status}`), {
        status: res.status, code, message: d?.message || null,
        newSession: (res.status === 409 && code === "max_messages_reached") || res.status === 404,
    });
}

export const submitInquiry = (formData) => authFetch("/inquiries/", { method: "POST", body: formData });
export const sendStt = (formData) => authFetch("/llm/stt", { method: "POST", body: formData });

// 토큰 없이 읽는 공개 콘텐츠(§14-4) — 실패해도 화면이 죽지 않게 빈 값
export async function getPublicList(url, params) {
    try { const r = await api.get(url, { params }); return Array.isArray(r.data) ? r.data : []; } catch { return []; }
}
export async function getPublicObject(url) {
    try { const r = await api.get(url); return r.data && typeof r.data === "object" && !Array.isArray(r.data) ? r.data : {}; } catch { return {}; }
}
// 게시 중인 공지만 온다(중요 먼저, 최신순) — 팝업은 중요만
export const getNotices = (importantOnly = false) =>
    getPublicList("/public/notices", { offset: 0, limit: 50 }).then((l) => (importantOnly ? l.filter((n) => n.is_important) : l));

// 내 문의 현황(§14-1) · 내 대화 기록(§14-5)
const list = (p) => p.then((r) => (Array.isArray(r.data) ? r.data : []));
export const getBadges = () => api.get("/auth/me/badges").then((r) => r.data);
export const getMyInquiries = () => list(api.get("/auth/me/inquiries"));
export const getMyInquiry = (id) => api.get(`/auth/me/inquiries/${id}`).then((r) => r.data);
export const markInquiryRead = (id) => api.post(`/auth/me/inquiries/${id}/read`).then(() => undefined, () => undefined);
export function appendInquiry(id, { text, files = [], captions = [] }) {
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    if (captions.some((c) => c && c.trim())) files.forEach((_, i) => fd.append("captions", captions[i] || ""));
    return api.post(`/auth/me/inquiries/${id}/append`, fd).then((r) => r.data);
}
export const getMySessions = () => list(api.get("/auth/me/sessions"));
export const getMySession = (id) => api.get(`/auth/me/sessions/${id}`).then((r) => r.data);
// 서버 사진 주소(/file/…)는 토큰 없이 열린다 — API 주소를 앞에 붙인다
export const fileUrl = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${process.env.REACT_APP_API_URL}${u.startsWith("/") ? "" : "/"}${u}`);
export function errCode(e) {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object" && !Array.isArray(d)) return d.code || null;
    return typeof d === "string" ? d : null;
}

// 메뉴 흐름 말 남기기(§14-3) — 자유 질문·음성은 서버가 스스로 남긴다. 실패해도 흐름은 계속.
export async function logMessage(sessionId, role, content) {
    if (!sessionId || !content) return;
    const body = { session_id: sessionId, role, content, ...(role === "assistant" ? { response_latency_ms: 0 } : {}) };
    try {
        await authFetch(`/chat/sessions/${sessionId}/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    } catch { /* 기록 실패는 무시 */ }
}

// 문의 접수(§14-2) — 던지지 않고 {ok, status, data, code} 로 돌려준다. 사업자번호는 서버가 토큰에서 읽는다.
export async function createInquiry({ content, quickCategoryId, inquiryType, chatSessionId, email, reopenedFromId, businessName, phone, files = [] }) {
    const fd = new FormData();
    fd.append("content", content);
    if (quickCategoryId) fd.append("quick_category_id", quickCategoryId);
    else if (inquiryType) fd.append("inquiry_type", inquiryType);
    if (chatSessionId) fd.append("chat_session_id", chatSessionId);
    if (email) fd.append("email", email);
    if (reopenedFromId) fd.append("reopened_from_id", reopenedFromId);
    if (businessName) fd.append("business_name", businessName);
    if (phone) fd.append("phone", phone);
    files.forEach((f) => fd.append("files", f));
    try {
        const res = await authFetch("/inquiries/", { method: "POST", body: fd });
        let data = null;
        try { data = await res.json(); } catch { /* 본문 없음 */ }
        return { ok: res.ok, status: res.status, data, code: res.ok ? null : errCode({ response: { data } }) };
    } catch {
        return { ok: false, status: 0, data: null, code: null };
    }
}
